/**
 * Tornado sensitivity solver — extracted from model.ts for Phase 15 (A2.1).
 *
 * Contains the tornado driver definitions, KPI picker, tornado runner, and
 * bar-encoding helper. computeCore is injected via a callback (same pattern as
 * break-even.ts) to avoid a circular runtime dependency on model.ts.
 *
 * Zero behaviour changes — pure extractive refactor.
 */

import {
  DEFAULT_CPI_ASSUMPTION,
  type Driver,
  type KPIs,
  type ModelResult,
  type Scenario,
  type TornadoRow,
} from "../types";

// Synchronous access — set by model.ts at module init via _setComputeCore().
let _computeCoreSync: ((s: Scenario) => ModelResult) | null = null;
export function _setComputeCore(fn: (s: Scenario) => ModelResult) {
  _computeCoreSync = fn;
}

function clone<T>(o: T): T {
  return structuredClone(o);
}

// ---------- tornado drivers ----------
export const DRIVERS: Driver[] = [
  {
    key: "tuitionGrowth",
    label: "Tuition growth",
    range: 0.5,
    apply: (s: Scenario, d: number) => {
      s.assumptions.tuitionGrowthPct *= 1 + d;
    },
  },
  {
    key: "rampYears",
    label: "Ramp speed (yrs)",
    range: 0.4,
    apply: (s: Scenario, d: number) => {
      s.assumptions.rampYears = Math.max(
        1,
        Math.round(s.assumptions.rampYears * (1 - d)),
      );
    },
  },
  {
    key: "rentIndexation",
    label: "Rent escalation",
    range: 0.5,
    // P7-R3 (#26): perturb the rate `effectiveRentGrowth` actually consumes for the active method —
    // for cpi/market scenarios the old `rentIndexationPct` flex was inert (the engine ignores it).
    apply: (s: Scenario, d: number) => {
      const m = s.assumptions.rentEscalationMethod ?? "fixed";
      if (m === "cpi")
        s.assumptions.cpiAssumption =
          (Number.isFinite(s.assumptions.cpiAssumption)
            ? (s.assumptions.cpiAssumption as number)
            : DEFAULT_CPI_ASSUMPTION) *
          (1 + d);
      else if (m === "market")
        s.assumptions.marketReviewPct =
          (Number.isFinite(s.assumptions.marketReviewPct)
            ? (s.assumptions.marketReviewPct as number)
            : 0.04) *
          (1 + d);
      else s.assumptions.rentIndexationPct *= 1 + d;
    },
  },
  {
    key: "devCapex",
    label: "Developer capex / sqm",
    range: 0.2,
    apply: (s: Scenario, d: number) => {
      s.buildings = s.buildings.map((b) => ({
        ...b,
        constructionPerSqmGfa: b.constructionPerSqmGfa * (1 + d),
      }));
    },
  },
  {
    key: "reddamCapex",
    label: "Operator capex allocation",
    range: 0.3,
    apply: (s: Scenario, d: number) => {
      s.assumptions.reddamFitoutAllowance *= 1 + d;
      s.buildings = s.buildings.map((b) => ({
        ...b,
        reddamCapex: b.reddamCapex * (1 + d),
      }));
    },
  },
  {
    key: "parkingRent",
    label: "Parking LL rent / bay",
    range: 0.4,
    // P4-R2: amenity LL rent drives developer NOI (operator hire/sublet income no longer does).
    apply: (s: Scenario, d: number) => {
      s.assumptions.parkingRentPerBay *= 1 + d;
    },
  },
  {
    key: "occupancy",
    label: "Steady-state utilisation",
    range: 0.2,
    apply: (s: Scenario, d: number) => {
      s.assumptions.steadyStateUtilisation = Math.min(
        1,
        Math.max(0, s.assumptions.steadyStateUtilisation * (1 + d)),
      );
    },
  },
  {
    key: "capRate",
    label: "Cap rate",
    range: 0.2,
    apply: (s: Scenario, d: number) => {
      s.assumptions.capRate = Math.max(0.001, s.assumptions.capRate * (1 + d));
    },
  },
  {
    key: "tuitionFee",
    label: "Tuition fee (Y1)",
    range: 0.2,
    apply: (s: Scenario, d: number) => {
      s.assumptions.feePerStudentYear1 *= 1 + d;
      s.assumptions.earlyLearningFeeYear1 *= 1 + d;
      s.assumptions.primaryFeeYear1 *= 1 + d;
      s.assumptions.secondaryFeeYear1 *= 1 + d;
      s.assumptions.seniorFeeYear1 *= 1 + d;
    },
  },
  {
    key: "startingUtilisation",
    label: "Opening utilisation",
    range: 0.3,
    apply: (s: Scenario, d: number) => {
      s.assumptions.startingUtilisation = Math.min(
        0.95,
        Math.max(0.05, s.assumptions.startingUtilisation * (1 + d)),
      );
    },
  },
  {
    key: "variableCost",
    label: "Variable cost / student",
    range: 0.2,
    apply: (s: Scenario, d: number) => {
      s.assumptions.variableCostPerStudent *= 1 + d;
    },
  },
  {
    key: "fixedOpex",
    label: "Fixed opex",
    range: 0.2,
    apply: (s: Scenario, d: number) => {
      s.assumptions.fixedOpex *= 1 + d;
    },
  },
  {
    key: "holdingInterest",
    label: "Holding interest rate",
    range: 0.5,
    apply: (s: Scenario, d: number) => {
      s.assumptions.holdingInterestRate = Math.max(
        0,
        s.assumptions.holdingInterestRate * (1 + d),
      );
    },
  },
  {
    key: "planningCosts",
    label: "Planning / approval costs",
    range: 0.5,
    apply: (s: Scenario, d: number) => {
      s.assumptions.planningApprovalCosts *= 1 + d;
    },
  },
  {
    key: "sportsFieldRent",
    label: "Sports field LL rent / m²",
    range: 0.4,
    // P4-R2: amenity LL rent drives developer NOI (operator hire income no longer does).
    apply: (s: Scenario, d: number) => {
      s.assumptions.sportsFieldRentPerSqm *= 1 + d;
    },
  },
  {
    key: "coveredPlayAreaGroundRent",
    label: "Covered play area ground rent",
    range: 0.4,
    // P4-R2: amenity LL rent drives developer NOI (operator hire income no longer does).
    apply: (s: Scenario, d: number) => {
      s.assumptions.coveredPlayAreaGroundRent *= 1 + d;
    },
  },
  {
    key: "constructionLvr",
    label: "Construction LVR",
    range: 0.15,
    apply: (s: Scenario, d: number) => {
      s.assumptions.constructionLvrRate = Math.max(
        0.3,
        Math.min(0.85, (s.assumptions.constructionLvrRate ?? 0.7) * (1 + d)),
      );
    },
  },
  {
    key: "constructionMargin",
    label: "Construction debt margin",
    range: 0.3,
    apply: (s: Scenario, d: number) => {
      s.assumptions.constructionMarginRate = Math.max(
        0,
        (s.assumptions.constructionMarginRate ?? 0.017) * (1 + d),
      );
    },
  },
  {
    key: "enrolmentCapacity",
    label: "Enrolment capacity",
    range: 0.25,
    apply: (s: Scenario, d: number) => {
      s.buildings = s.buildings.map((b) => ({
        ...b,
        enrolmentCapacity: Math.max(
          1,
          Math.round(b.enrolmentCapacity * (1 + d)),
        ),
      }));
    },
  },
  {
    key: "termLoanHold",
    label: "Hold period (term loan)",
    range: 0.4,
    apply: (s: Scenario, d: number) => {
      s.assumptions.termLoanHoldYears = Math.max(
        1,
        (s.assumptions.termLoanHoldYears ?? 5) * (1 + d),
      );
    },
  },
  {
    key: "constructionInterestRate",
    label: "Construction interest rate",
    range: 0.3,
    apply: (s: Scenario, d: number) => {
      s.assumptions.holdingInterestRate = Math.max(
        0,
        s.assumptions.holdingInterestRate * (1 + d),
      );
      s.assumptions.constructionMarginRate = Math.max(
        0,
        (s.assumptions.constructionMarginRate ?? 0.017) * (1 + d),
      );
    },
  },
];

// ---------- KPI picker ----------
export function pickKpi(r: ModelResult, kpi: keyof KPIs): number {
  // W4 latent: operator rent-pct KPIs (rentPctRevenueYear1 / rentPctRevenueSteadyState /
  // rentPctRevenueAvg) are intentionally not wired here, so tornado() returns zero base for them.
  // Promote only if the UI starts offering them as sensitivity targets.
  switch (kpi) {
    case "developerIrr":
      return r.kpis.developerIrr;
    case "developerUngearedEarlyExitIrr":
      return r.kpis.developerUngearedEarlyExitIrr;
    case "developerMarginOnCost":
      return r.kpis.developerMarginOnCost;
    case "developerYieldOnCost":
      return r.kpis.developerYieldOnCost;
    case "developerGearedIrr":
      return r.kpis.developerGearedIrr;
    case "developerGearedEarlyExitIrr":
      return r.kpis.developerGearedEarlyExitIrr;
    case "developerGearedWholeOfLifeIrr":
      return r.kpis.developerGearedWholeOfLifeIrr;
    case "developerYieldOnCash":
      return r.kpis.developerYieldOnCash;
    case "reddamNpv":
      return r.kpis.reddamNpv;
    case "avgEbitda":
      return r.kpis.avgEbitda;
  }
  return 0;
}

// ---------- tornado runner ----------
export function tornado(scenario: Scenario, kpi?: keyof KPIs): TornadoRow[] {
  if (!scenario?.assumptions || !Array.isArray(scenario.buildings)) return [];
  if (!kpi) kpi = "developerGearedIrr";
  // Hot loop: every tornado KPI supported by pickKpi is produced by computeCore, so use it
  // directly and skip the two break-even bisection solvers the public compute() wrapper runs
  // (perf — each compute() is otherwise ~127× a single computeCore).
  const computeCore = _computeCoreSync!;
  const baseResult = computeCore(scenario);
  const base = pickKpi(baseResult, kpi);
  return DRIVERS.map((d) => {
    const lo = clone(scenario);
    d.apply(lo, -d.range);
    const hi = clone(scenario);
    d.apply(hi, d.range);
    const loResult = computeCore(lo);
    const hiResult = computeCore(hi);
    return {
      variable: d.key,
      label: d.label,
      low: pickKpi(loResult, kpi),
      high: pickKpi(hiResult, kpi),
      base,
    };
  }).sort((a, b) => Math.abs(b.high - b.low) - Math.abs(a.high - a.low));
}

/** P7-R1 (#14/#15): tornado bar encoding by OUTCOME, not flex direction. Every tornado KPI is
 *  higher-is-better, so the WORSE endpoint is the lower KPI (drawn red / left) and the BETTER endpoint is
 *  the higher KPI (green / right) — regardless of whether the −flex or +flex produced it. This fixes cost
 *  drivers (e.g. developer capex) whose +flex lowers IRR but was previously drawn green/right. `spread` is
 *  always non-negative. */
export function tornadoBars(row: TornadoRow): {
  worse: number;
  better: number;
  spread: number;
} {
  const worse = Math.min(row.low, row.high);
  const better = Math.max(row.low, row.high);
  return { worse, better, spread: Math.abs(row.high - row.low) };
}
